/* ==========================================================================
   VSR Interactive — scroll-driven behaviour for voyagersoundrecords.com
   Adds: hero parallax, scroll progress, reveal-on-scroll, stat count-up,
         card detail states.
   Pairs with vsr-interactive.css. No dependencies.

   Design note: vsr-premium.css already runs 14 infinite keyframe animations.
   Everything here is SCROLL-LINKED rather than looping, so it adds motion
   that responds to the reader instead of competing with what's already moving.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var docEl = document.documentElement;

  /* ---------- shared rAF scroll loop (one listener, not five) ---------- */
  var handlers = [];
  var ticking = false;

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(function () {
      var y = window.pageYOffset || docEl.scrollTop;
      for (var i = 0; i < handlers.length; i++) {
        try { handlers[i](y); } catch (e) { /* never let one break the rest */ }
      }
      ticking = false;
    });
  }

  function addScroll(fn) {
    handlers.push(fn);
    fn(window.pageYOffset || docEl.scrollTop);
  }

  /* ---------- 1. scroll progress rail ---------- */
  function initProgress() {
    var bar = document.createElement('div');
    bar.className = 'vsr-progress';
    bar.innerHTML = '<span class="vsr-progress-fill"></span>';
    document.body.appendChild(bar);
    var fill = bar.firstChild;

    addScroll(function (y) {
      var max = docEl.scrollHeight - window.innerHeight;
      var pct = max > 0 ? (y / max) * 100 : 0;
      fill.style.width = pct.toFixed(2) + '%';
    });
  }

  /* ---------- 2. hero parallax + content departure ---------- */
  function initHero() {
    var wrap = document.querySelector('.vsr-hero-wrap');
    if (!wrap) return;

    var video   = wrap.querySelector('.vsr-hero-video');
    var content = wrap.querySelector('.vsr-hero-content');
    var cue     = wrap.querySelector('.vsr-hero-scroll');
    var overlay = wrap.querySelector('.vsr-hero-overlay');

    wrap.classList.add('vsr-hero-ready');

    if (reduced) return;

    // .vsr-hero-content is centred with `transform: translate(-50%, -50%)`.
    // Writing transform from JS would wipe that and throw the title off-centre,
    // so capture the base transform and always compose on top of it.
    var baseTransform = '';
    if (content) {
      var cs = window.getComputedStyle(content).transform;
      baseTransform = (cs && cs !== 'none') ? cs + ' ' : '';
    }
    // The video fills the wrap exactly, so translating it down would expose a
    // gap at the top. Start slightly scaled up to give the drift headroom.
    var VIDEO_BASE_SCALE = 1.16;
    if (video) video.style.transform = 'scale(' + VIDEO_BASE_SCALE + ')';

    addScroll(function (y) {
      var h = wrap.offsetHeight || window.innerHeight;
      if (y > h * 1.2) return;              // stop work once hero is gone
      var p = Math.min(y / h, 1);           // 0 → 1 through the hero

      // Background drifts slower than the page — classic depth cue.
      if (video) {
        video.style.transform =
          'translate3d(0,' + (y * 0.30).toFixed(1) + 'px,0) scale(' +
          (VIDEO_BASE_SCALE + p * 0.10).toFixed(4) + ')';
      }
      // Title rises and dissolves rather than simply scrolling away.
      if (content) {
        content.style.transform =
          baseTransform + 'translate3d(0,' + (-y * 0.22).toFixed(1) + 'px,0)';
        content.style.opacity = Math.max(0, 1 - p * 1.55).toFixed(3);
      }
      // Overlay deepens so incoming content has contrast to land on.
      if (overlay) overlay.style.opacity = (0.55 + p * 0.45).toFixed(3);
      // The cue is only useful before you've scrolled.
      if (cue) {
        cue.style.opacity = Math.max(0, 1 - p * 3.2).toFixed(3);
        cue.style.pointerEvents = p > 0.3 ? 'none' : '';
      }
    });

    if (cue) {
      cue.addEventListener('click', function () {
        var target = wrap.nextElementSibling;
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      cue.setAttribute('role', 'button');
      cue.setAttribute('tabindex', '0');
      cue.setAttribute('aria-label', 'Scroll to catalogue');
      cue.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); cue.click(); }
      });
    }
  }

  /* ---------- 3. reveal on scroll ---------- */
  function initReveal() {
    var targets = document.querySelectorAll(
      '.vsr-stats, ' +
      '.shopify-section .collection, ' +
      '.shopify-section .card-wrapper, ' +
      '.vsr-reveal, ' +
      '.shopify-section h2, ' +
      '.shopify-section .rich-text__blocks'
    );
    if (!targets.length) return;

    if (reduced || !('IntersectionObserver' in window)) {
      for (var i = 0; i < targets.length; i++) targets[i].classList.add('vsr-in');
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        // Stagger siblings so a product grid arrives as a wave, not a slab.
        var sibs = el.parentElement ? Array.prototype.indexOf.call(el.parentElement.children, el) : 0;
        el.style.transitionDelay = Math.min(sibs * 60, 420) + 'ms';
        el.classList.add('vsr-in');
        io.unobserve(el);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });

    for (var j = 0; j < targets.length; j++) {
      targets[j].classList.add('vsr-will-reveal');
      io.observe(targets[j]);
    }
  }

  /* ---------- 4. stat count-up ---------- */
  function initCounters() {
    var nums = document.querySelectorAll('.vsr-stat-num');
    if (!nums.length) return;

    function run(el) {
      var raw = (el.textContent || '').trim();
      var m = raw.match(/^(\d[\d\s.,]*)(.*)$/);
      if (!m) return;                                  // "∞" and friends stay put
      var target = parseFloat(m[1].replace(/[\s,]/g, ''));
      if (!isFinite(target)) return;
      var suffix = m[2] || '';
      if (reduced) { el.textContent = target + suffix; return; }

      var dur = 1500, t0 = null;
      el.classList.add('vsr-counting');
      function step(ts) {
        if (t0 === null) t0 = ts;
        var p = Math.min((ts - t0) / dur, 1);
        var eased = 1 - Math.pow(1 - p, 3);            // ease-out cubic
        el.textContent = Math.round(target * eased) + suffix;
        if (p < 1) requestAnimationFrame(step);
        else { el.textContent = target + suffix; el.classList.remove('vsr-counting'); }
      }
      requestAnimationFrame(step);
    }

    if (!('IntersectionObserver' in window)) {
      for (var i = 0; i < nums.length; i++) run(nums[i]);
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { run(e.target); io.unobserve(e.target); }
      });
    }, { threshold: 0.5 });
    for (var k = 0; k < nums.length; k++) io.observe(nums[k]);
  }

  /* ---------- 5. product card detail ---------- */
  function initCards() {
    if (reduced) return;
    var cards = document.querySelectorAll('.card-wrapper, .card__inner');
    Array.prototype.forEach.call(cards, function (card) {
      card.classList.add('vsr-card-fx');
      card.addEventListener('pointermove', function (e) {
        var r = card.getBoundingClientRect();
        // Feed the pointer position to CSS so the sheen tracks the cursor.
        card.style.setProperty('--vsr-mx', ((e.clientX - r.left) / r.width * 100).toFixed(1) + '%');
        card.style.setProperty('--vsr-my', ((e.clientY - r.top) / r.height * 100).toFixed(1) + '%');
      }, { passive: true });
      card.addEventListener('pointerleave', function () {
        card.style.removeProperty('--vsr-mx');
        card.style.removeProperty('--vsr-my');
      }, { passive: true });
    });
  }

  /* ---------- 6. header condenses past the hero ---------- */
  function initHeader() {
    var header = document.querySelector('.header-wrapper, .section-header, header.header');
    if (!header) return;
    addScroll(function (y) {
      header.classList.toggle('vsr-header-condensed', y > 120);
    });
  }

  function boot() {
    initProgress();
    initHero();
    initReveal();
    initCounters();
    initCards();
    initHeader();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    docEl.classList.add('vsr-interactive-on');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
